import { useSyncExternalStore } from 'react';
import type { LangCode, LanguageMeta } from '@/lib/multilingual';
import { LANGUAGES } from '@/lib/multilingual';

/**
 * A tiny external store for the reader's *previewed* reading language.
 *
 * This only switches which prewritten sample version a page shows — it does NOT
 * translate the whole site. The header selector makes that explicit. The value
 * is a UI preference only and is stored in localStorage (never sent anywhere).
 */

const KEY = 'gs-lang-preview';

function readInitial(): LangCode {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(KEY);
  return stored === 'es' ? 'es' : 'en';
}

let current: LangCode = readInitial();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function setPreviewLanguage(code: LangCode): void {
  current = code;
  try {
    window.localStorage.setItem(KEY, code);
  } catch {
    /* storage unavailable — keep in-memory value */
  }
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): LangCode {
  return current;
}

function getServerSnapshot(): LangCode {
  return 'en';
}

export function usePreviewLanguage(): LangCode {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function activeLanguages(): LanguageMeta[] {
  return LANGUAGES.filter((l) => l.status === 'live');
}